import type { Recording } from '../types.ts';

/** Directional room estimate beside a source edge, including reviewed interior cuts. */
export function edgeToneRms(
  rec: Recording, sourceSec: number | undefined, side: 'head' | 'tail',
): number | undefined {
  const points = rec.toneLevels;
  if (sourceSec === undefined || !points?.length) return undefined;
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (points[mid][0] < sourceSec) lo = mid + 1;
    else hi = mid;
  }
  const column = side === 'head' ? 1 : 2;
  const candidates = [points[lo - 1], points[lo]].filter((p) =>
    p && Math.abs(p[0] - sourceSec) <= 0.501 && p[column] !== null
      && Number.isFinite(p[column]) && p[column]! > 0);
  candidates.sort((a, b) => Math.abs(a[0] - sourceSec) - Math.abs(b[0] - sourceSec));
  return candidates[0]?.[column] ?? undefined;
}

/**
 * The existing native wire has one constant RMS per op. The geometric mean splits
 * the dB error equally between both edges, minimising the worst step. A missing
 * edge uses its measured neighbour; legacy manifests retain the profile's level.
 */
export function gapToneRms(
  rec: Recording, beforeSec: number | undefined, afterSec: number | undefined,
): number | undefined {
  const before = edgeToneRms(rec, beforeSec, 'tail');
  const after = edgeToneRms(rec, afterSec, 'head');
  if (before !== undefined && after !== undefined) return Math.sqrt(before * after);
  return before ?? after;
}
